import { useState, useRef, useCallback, type DragEvent } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { RefreshCw, Trash2, Heart, Shield, Store, Key, Upload, Zap, Search, X, ChevronRight, Eye, Terminal } from 'lucide-react';
import { settingsApi, type Patch } from '../../api/endpoints/settings';
import { queryClient } from '../../providers/QueryProvider';
import { useAuth } from '../../providers/AuthProvider';
import { useDebounce } from '../../hooks/useDebounce';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { formatDate } from '../../utils/formatters';

export function SettingsPage() {
  const { user, hasPerm } = useAuth();
  const [refreshMsg, setRefreshMsg] = useState('');
  const [clearMsg, setClearMsg] = useState('');

  const { data: vendorData, isLoading: vendorLoading } = useQuery({
    queryKey: ['settings', 'vendor-portal'],
    queryFn: () => settingsApi.getVendorPortal(),
  });

  const { data: secretData, isLoading: secretLoading } = useQuery({
    queryKey: ['settings', 'deployment-secret'],
    queryFn: () => settingsApi.getDeploymentSecret(),
  });

  const toggleVendorMutation = useMutation({
    mutationFn: (enabled: boolean) => settingsApi.toggleVendorPortal(enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'vendor-portal'] });
    },
  });

  const handleRefreshStatus = async () => {
    try {
      setRefreshMsg('Refreshing...');
      const result = await settingsApi.health();
      setRefreshMsg(`Status: ${result.status}, Uptime: ${Math.floor(result.uptime)}s`);
      setTimeout(() => setRefreshMsg(''), 5000);
    } catch {
      setRefreshMsg('Failed to refresh status');
      setTimeout(() => setRefreshMsg(''), 3000);
    }
  };

  const handleClearLogs = () => {
    setClearMsg('Log cleanup triggered');
    setTimeout(() => setClearMsg(''), 3000);
  };

  const vendorEnabled = vendorData?.enabled ?? false;

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-lg font-semibold text-gray-800 dark:text-white">Settings</h1>
        <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">System configuration and quick actions</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Current Configuration */}
        <Card title="Current Configuration">
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">MongoDB URI</span>
              <span className="text-xs text-gray-700 dark:text-gray-300 font-mono">mongodb://localhost:27017/****</span>
            </div>
            <div className="border-t border-gray-100 dark:border-gray-800" />
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">SSH Log Path</span>
              <span className="text-xs text-gray-700 dark:text-gray-300 font-mono">/var/log/auth.log</span>
            </div>
            <div className="border-t border-gray-100 dark:border-gray-800" />
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Tunnel Log Path</span>
              <span className="text-xs text-gray-700 dark:text-gray-300 font-mono">/var/log/tunnel.log</span>
            </div>
            <div className="border-t border-gray-100 dark:border-gray-800" />
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Refresh Interval</span>
              <span className="text-xs text-gray-700 dark:text-gray-300">30 seconds</span>
            </div>
          </div>
        </Card>

        {/* Quick Actions */}
        <Card title="Quick Actions">
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RefreshCw size={14} className="text-blue-500" />
                <span className="text-xs text-gray-700 dark:text-gray-300">Refresh Connection Status</span>
              </div>
              <button
                onClick={handleRefreshStatus}
                className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors"
              >
                Refresh
              </button>
            </div>
            {refreshMsg && (
              <p className="text-xs text-blue-600 dark:text-blue-400 ml-6">{refreshMsg}</p>
            )}

            <div className="border-t border-gray-100 dark:border-gray-800" />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trash2 size={14} className="text-red-500" />
                <span className="text-xs text-gray-700 dark:text-gray-300">Clear Old Logs</span>
              </div>
              <button
                onClick={handleClearLogs}
                className="px-3 py-1 bg-red-600 text-white text-xs rounded-md hover:bg-red-700 transition-colors"
              >
                Clear
              </button>
            </div>
            {clearMsg && (
              <p className="text-xs text-red-600 dark:text-red-400 ml-6">{clearMsg}</p>
            )}

            <div className="border-t border-gray-100 dark:border-gray-800" />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Heart size={14} className="text-green-500" />
                <span className="text-xs text-gray-700 dark:text-gray-300">Health Check</span>
              </div>
              <Link
                to="/api/health"
                target="_blank"
                className="px-3 py-1 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 transition-colors"
              >
                Check
              </Link>
            </div>

            {user?.role === 'admin' && (
              <>
                <div className="border-t border-gray-100 dark:border-gray-800" />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield size={14} className="text-purple-500" />
                    <span className="text-xs text-gray-700 dark:text-gray-300">Manage Role Permissions</span>
                  </div>
                  <Link
                    to="/roles"
                    className="px-3 py-1 bg-purple-600 text-white text-xs rounded-md hover:bg-purple-700 transition-colors"
                  >
                    Manage
                  </Link>
                </div>
              </>
            )}
          </div>
        </Card>

        {/* Vendor Portal */}
        {hasPerm('settings') && (
          <Card title="Vendor Portal">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Store size={18} className="text-teal-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Vendor Portal Access</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Allow vendors to access their portal and view assigned devices
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={vendorEnabled ? 'green' : 'gray'} dot>
                    {vendorEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                  <button
                    onClick={() => toggleVendorMutation.mutate(!vendorEnabled)}
                    disabled={vendorLoading || toggleVendorMutation.isPending}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      vendorEnabled ? 'bg-teal-600' : 'bg-gray-300 dark:bg-gray-600'
                    } ${(vendorLoading || toggleVendorMutation.isPending) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        vendorEnabled ? 'translate-x-4' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Deployment Secret */}
        {hasPerm('settings') && (
          <Card title="Deployment Secret">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Key size={18} className="text-yellow-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Encryption Secret</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      AES-256-CBC secret for encrypting deployment codes in partner API responses
                    </p>
                  </div>
                </div>
                <Badge
                  variant={secretLoading ? 'gray' : secretData?.configured ? 'green' : 'red'}
                  dot
                >
                  {secretLoading
                    ? 'Loading...'
                    : secretData?.configured
                      ? `Set (${secretData.length} chars)`
                      : 'Not Set'}
                </Badge>
              </div>
              {!secretLoading && !secretData?.configured && (
                <p className="mt-3 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-md px-3 py-2">
                  A deployment secret (min 8 characters) must be configured before partner API responses include deployment codes.
                </p>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Remote Scripts - Admin only */}
      {user?.role === 'admin' && <RemoteScripts />}
    </div>
  );
}

// =============================================
// Remote Scripts Section
// =============================================
function RemoteScripts() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [scriptContent, setScriptContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [scriptName, setScriptName] = useState('');
  const [scriptDesc, setScriptDesc] = useState('');
  const [targetType, setTargetType] = useState<'all' | 'selected'>('all');
  const [selectedClients, setSelectedClients] = useState<Array<{ id: string; name: string; deployment?: string }>>([]);
  const [clientSearch, setClientSearch] = useState('');
  // Filter fields for dual-pane picker
  const [filterName, setFilterName] = useState('');
  const [filterSubdomain, setFilterSubdomain] = useState('');
  const [filterDeployment, setFilterDeployment] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [scriptMode, setScriptMode] = useState<'upload' | 'write' | 'file'>('upload');
  // File upload to device state
  const [uploadFile, setUploadFile] = useState<{ name: string; b64: string; size: number } | null>(null);
  const [uploadDest, setUploadDest] = useState('');
  const [uploadMakeExec, setUploadMakeExec] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [viewingPatch, setViewingPatch] = useState<Patch | null>(null);
  const [viewingLogs, setViewingLogs] = useState<string | null>(null); // patch_name

  const { data: logsData } = useQuery({
    queryKey: ['patch-logs', viewingLogs],
    queryFn: () => settingsApi.patchLogs({ patch_name: viewingLogs!, limit: 100 }),
    enabled: !!viewingLogs,
  });

  const debouncedSearch = useDebounce(clientSearch, 300);
  const debouncedFilterName = useDebounce(filterName, 300);
  const debouncedFilterSub = useDebounce(filterSubdomain, 300);
  const debouncedFilterDep = useDebounce(filterDeployment, 300);

  const { data: patchesData, refetch: refetchPatches } = useQuery({
    queryKey: ['patches'],
    queryFn: () => settingsApi.listPatches(),
  });

  const { data: searchResults } = useQuery({
    queryKey: ['client-search', debouncedSearch],
    queryFn: () => settingsApi.searchClients(debouncedSearch),
    enabled: debouncedSearch.length >= 2,
  });

  // Filtered clients for dual-pane picker
  const hasFilters = debouncedFilterName.length >= 2 || debouncedFilterSub.length >= 2 || debouncedFilterDep.length >= 2;
  const { data: filteredResults } = useQuery({
    queryKey: ['client-filter', debouncedFilterName, debouncedFilterSub, debouncedFilterDep],
    queryFn: () => settingsApi.searchClients('', {
      name: debouncedFilterName || undefined,
      subdomain: debouncedFilterSub || undefined,
      deployment: debouncedFilterDep || undefined,
    }),
    enabled: hasFilters,
  });

  const patches = patchesData?.patches || [];

  const handleFile = useCallback((file: File) => {
    if (!file.name.endsWith('.sh')) { alert('Please select a .sh file'); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setScriptContent(content);
      setFileName(`${file.name} (${(content.length / 1024).toFixed(1)} KB)`);
      if (!scriptName) {
        setScriptName(file.name.replace(/\.sh$/, '').replace(/[\s_]+/g, '-').toLowerCase());
      }
    };
    reader.readAsText(file);
  }, [scriptName]);

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) handleFile(e.dataTransfer.files[0]);
  };

  const handleUploadFile = (file: File) => {
    if (file.size > 10 * 1024 * 1024) { alert('File too large (max 10MB)'); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const b64 = (e.target?.result as string).split(',')[1];
      setUploadFile({ name: file.name, b64, size: file.size });
      if (!scriptName) setScriptName(`upload-${file.name.replace(/[^a-z0-9.-]/gi, '-').toLowerCase()}`);
      if (!uploadDest) setUploadDest(`/tmp/${file.name}`);
    };
    reader.readAsDataURL(file);
  };

  const canDeploy = scriptMode === 'file'
    ? (uploadFile && uploadDest.trim() && scriptName.trim() && (targetType === 'all' || selectedClients.length > 0))
    : (scriptContent && scriptName.trim() && (targetType === 'all' || selectedClients.length > 0));

  const handleDeploy = async () => {
    if (!canDeploy) return;
    setDeploying(true);
    try {
      const target = targetType === 'all' ? 'all' : selectedClients.map(c => c.id).join(',');

      // For file upload mode, generate a script that writes the file
      let finalScript = scriptContent;
      if (scriptMode === 'file' && uploadFile) {
        const dest = uploadDest.trim();
        const dir = dest.substring(0, dest.lastIndexOf('/'));
        finalScript = `#!/bin/bash\nmkdir -p "${dir}"\necho '${uploadFile.b64}' | base64 -d > "${dest}"\n${uploadMakeExec ? `chmod +x "${dest}"` : ''}\necho "File uploaded to ${dest} (${uploadFile.size} bytes)"`;
      }

      const result = await settingsApi.deployPatch({
        name: scriptName.trim(),
        description: scriptDesc.trim() || (scriptMode === 'file' ? `Upload ${uploadFile?.name} to ${uploadDest}` : ''),
        script: finalScript,
        target,
      });
      if (result.success) {
        setScriptContent('');
        setFileName('');
        setScriptName('');
        setScriptDesc('');
        setTargetType('all');
        setSelectedClients([]);
        setShowPreview(false);
        setUploadFile(null);
        setUploadDest('');
        setUploadMakeExec(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (uploadInputRef.current) uploadInputRef.current.value = '';
        refetchPatches();
        alert('Script deployed successfully');
      }
    } catch (err) {
      alert('Deploy failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setDeploying(false);
    }
  };

  const handleDeactivate = async (name: string) => {
    if (!confirm(`Deactivate script "${name}"? Clients that have not yet applied it will no longer receive it.`)) return;
    try {
      await settingsApi.deactivatePatch(name);
      refetchPatches();
    } catch (err) {
      alert('Error: ' + (err instanceof Error ? err.message : 'Unknown'));
    }
  };

  const addClient = (id: string, name: string, deployment?: string) => {
    if (selectedClients.some(c => c.id === id)) return;
    setSelectedClients(prev => [...prev, { id, name, deployment }]);
    setClientSearch('');
  };

  const removeClient = (id: string) => {
    setSelectedClients(prev => prev.filter(c => c.id !== id));
  };

  return (
    <div className="mt-6">
      <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-1">Remote Scripts</h2>
      <p className="text-gray-500 dark:text-gray-400 text-xs mb-4">Deploy shell scripts to connected tunnel clients. Scripts are delivered via the monitoring agent and executed automatically.</p>

      {/* Deploy New Script */}
      <Card title="Deploy New Script">
        <div className="p-4">
          {/* Script source tabs */}
          <div className="flex items-center gap-1 mb-3 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => { setScriptMode('upload'); setScriptContent(''); setFileName(''); }}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
                scriptMode === 'upload'
                  ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Upload size={12} className="inline mr-1" />Upload File
            </button>
            <button
              onClick={() => { setScriptMode('write'); setFileName(''); }}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
                scriptMode === 'write'
                  ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Terminal size={12} className="inline mr-1" />Write Script
            </button>
            <button
              onClick={() => { setScriptMode('file'); setScriptContent(''); setFileName(''); }}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
                scriptMode === 'file'
                  ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Upload size={12} className="inline mr-1" />Upload to Device
            </button>
          </div>

          {scriptMode === 'upload' ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors mb-4 ${
                dragOver
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-purple-400'
              }`}
            >
              <Upload size={28} className="mx-auto text-gray-400 mb-2" />
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Drag and drop a <span className="font-semibold">.sh</span> file here, or <span className="text-purple-600 dark:text-purple-400 font-medium">click to browse</span>
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{fileName || 'No file selected'}</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".sh"
                className="hidden"
                onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
              />
            </div>
          ) : scriptMode === 'write' ? (
            <div className="mb-4">
              <textarea
                value={scriptContent}
                onChange={(e) => setScriptContent(e.target.value)}
                placeholder={'#!/bin/bash\n# Write your script here...\n\necho "Hello from remote device"'}
                spellCheck={false}
                className="w-full h-40 px-3 py-2 bg-gray-900 text-green-400 font-mono text-xs rounded-lg border border-gray-700 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 resize-y"
              />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {scriptContent ? `${scriptContent.split('\n').length} lines, ${(scriptContent.length / 1024).toFixed(1)} KB` : 'Write a shell script to execute on target devices'}
              </p>
            </div>
          ) : (
            <div className="mb-4 space-y-3">
              <div
                onClick={() => uploadInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  uploadFile ? 'border-green-400 bg-green-50 dark:bg-green-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-purple-400'
                }`}
              >
                <Upload size={24} className={`mx-auto mb-1 ${uploadFile ? 'text-green-500' : 'text-gray-400'}`} />
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {uploadFile
                    ? <><span className="font-semibold text-green-700 dark:text-green-400">{uploadFile.name}</span> ({(uploadFile.size / 1024).toFixed(1)} KB)</>
                    : <>Select any file to upload to device (max 10MB)</>
                  }
                </p>
                <input
                  ref={uploadInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => { if (e.target.files?.[0]) handleUploadFile(e.target.files[0]); }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Destination Path on Device</label>
                <input
                  type="text"
                  value={uploadDest}
                  onChange={(e) => setUploadDest(e.target.value)}
                  placeholder="/opt/restoroworks-connect/myfile.conf"
                  className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs font-mono dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-purple-500"
                />
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={uploadMakeExec} onChange={e => setUploadMakeExec(e.target.checked)} className="text-purple-600 focus:ring-purple-500 rounded" />
                <span className="ml-2 text-xs text-gray-700 dark:text-gray-300">Make executable (chmod +x)</span>
              </label>
            </div>
          )}

          {/* Name & Description */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Script Name</label>
              <input
                type="text"
                value={scriptName}
                onChange={(e) => setScriptName(e.target.value)}
                placeholder="e.g. fix-tunnel-restart"
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <input
                type="text"
                value={scriptDesc}
                onChange={(e) => setScriptDesc(e.target.value)}
                placeholder="Brief description of what this script does"
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-purple-500"
              />
            </div>
          </div>

          {/* Target */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Target Devices</label>
            <div className="flex items-center gap-4 mb-2">
              <label className="inline-flex items-center cursor-pointer">
                <input type="radio" checked={targetType === 'all'} onChange={() => setTargetType('all')} className="text-purple-600 focus:ring-purple-500" />
                <span className="ml-2 text-xs text-gray-700 dark:text-gray-300">All connected devices</span>
              </label>
              <label className="inline-flex items-center cursor-pointer">
                <input type="radio" checked={targetType === 'selected'} onChange={() => setTargetType('selected')} className="text-purple-600 focus:ring-purple-500" />
                <span className="ml-2 text-xs text-gray-700 dark:text-gray-300">Selected devices</span>
              </label>
            </div>

            {targetType === 'selected' && (
              <div>
                {/* Filter bar */}
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <input type="text" value={filterName} onChange={e => setFilterName(e.target.value)} placeholder="Filter by name..." className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-purple-500" />
                  <input type="text" value={filterSubdomain} onChange={e => setFilterSubdomain(e.target.value)} placeholder="Filter by subdomain..." className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-purple-500" />
                  <input type="text" value={filterDeployment} onChange={e => setFilterDeployment(e.target.value)} placeholder="Filter by deployment..." className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-purple-500" />
                </div>

                {/* Dual-pane picker */}
                <div className="grid grid-cols-2 gap-3">
                  {/* LEFT: Filtered results */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="bg-gray-50 dark:bg-gray-800 px-3 py-1.5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        Available ({filteredResults?.clients?.filter(c => !selectedClients.some(s => s.id === c.client_id)).length || 0})
                      </span>
                      {hasFilters && filteredResults?.clients && (
                        <button
                          onClick={() => {
                            const available = filteredResults.clients.filter(c => !selectedClients.some(s => s.id === c.client_id));
                            setSelectedClients(prev => [...prev, ...available.map(c => ({ id: c.client_id, name: c.client_name, deployment: c.deployment_name }))]);
                          }}
                          className="text-xs text-purple-600 hover:text-purple-800 dark:text-purple-400 font-medium"
                        >
                          Add all &raquo;
                        </button>
                      )}
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {!hasFilters ? (
                        <p className="text-xs text-gray-400 dark:text-gray-500 p-3 text-center">Type at least 2 characters in any filter</p>
                      ) : !filteredResults?.clients?.length ? (
                        <p className="text-xs text-gray-400 dark:text-gray-500 p-3 text-center">No devices match</p>
                      ) : (
                        filteredResults.clients.map(c => {
                          const already = selectedClients.some(s => s.id === c.client_id);
                          if (already) return null;
                          return (
                            <div
                              key={c.client_id}
                              onClick={() => addClient(c.client_id, c.client_name, c.deployment_name)}
                              className="px-3 py-1.5 hover:bg-purple-50 dark:hover:bg-purple-900/20 cursor-pointer flex items-center gap-2 text-xs border-b border-gray-100 dark:border-gray-800 last:border-0"
                            >
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.status === 'connected' ? 'bg-green-500' : 'bg-red-500'}`} />
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-gray-800 dark:text-gray-200 truncate">{c.client_name}</div>
                                {c.deployment_name && <div className="text-[10px] text-gray-400 truncate">{c.deployment_name}</div>}
                                {c.subdomain && <div className="text-[10px] text-gray-400 truncate">{c.subdomain}</div>}
                              </div>
                              <span className="text-purple-500 flex-shrink-0">+</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* RIGHT: Selected devices */}
                  <div className="border border-purple-200 dark:border-purple-800 rounded-lg">
                    <div className="bg-purple-50 dark:bg-purple-900/20 px-3 py-1.5 border-b border-purple-200 dark:border-purple-800 flex items-center justify-between">
                      <span className="text-xs font-medium text-purple-700 dark:text-purple-300">
                        Selected ({selectedClients.length})
                      </span>
                      {selectedClients.length > 0 && (
                        <button onClick={() => setSelectedClients([])} className="text-xs text-red-500 hover:text-red-700 font-medium">
                          Clear all
                        </button>
                      )}
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {selectedClients.length === 0 ? (
                        <p className="text-xs text-gray-400 dark:text-gray-500 p-3 text-center">No devices selected</p>
                      ) : (
                        selectedClients.map(c => (
                          <div key={c.id} className="px-3 py-1.5 flex items-center justify-between text-xs border-b border-purple-100 dark:border-purple-900 last:border-0">
                            <div className="min-w-0 flex-1">
                              <span className="font-medium text-purple-800 dark:text-purple-200 truncate block">{c.name}</span>
                              {c.deployment && <span className="text-[10px] text-purple-500 dark:text-purple-400 truncate block">{c.deployment}</span>}
                            </div>
                            <button onClick={() => removeClient(c.id)} className="text-red-400 hover:text-red-600 flex-shrink-0 ml-2">
                              <X size={14} />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Preview */}
          {scriptContent && (
            <div className="mb-3">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="text-xs text-purple-600 dark:text-purple-400 font-medium flex items-center gap-1"
              >
                <ChevronRight size={12} className={`transition-transform ${showPreview ? 'rotate-90' : ''}`} />
                Preview Script
              </button>
              {showPreview && (
                <pre className="mt-2 bg-gray-900 text-green-400 rounded-lg p-4 overflow-x-auto text-xs font-mono max-h-60 overflow-y-auto">
                  <code>{scriptContent}</code>
                </pre>
              )}
            </div>
          )}

          {/* Deploy button */}
          <button
            onClick={handleDeploy}
            disabled={!canDeploy || deploying}
            className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors flex items-center gap-2 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Zap size={14} />
            {deploying ? 'Deploying...' : 'Deploy Script'}
          </button>
        </div>
      </Card>

      {/* Deployed Scripts Table */}
      <div className="mt-4">
        <Card
          title="Deployed Scripts"
          action={
            <button onClick={() => refetchPatches()} className="text-purple-600 hover:text-purple-700 dark:text-purple-400 text-xs flex items-center gap-1">
              <RefreshCw size={12} /> Refresh
            </button>
          }
        >
          <Table>
            <Thead>
              <tr>
                <Th>Name</Th>
                <Th>Description</Th>
                <Th>Target</Th>
                <Th>Rollout</Th>
                <Th>Created</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </Thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {patches.map((p) => {
                const targetLabel = p.target === 'all' ? 'All devices' : `${p.target.split(',').length} devices`;
                const pct = p.total_target > 0 ? Math.round((p.applied_count / p.total_target) * 100) : 0;
                const progressColor = pct >= 100 ? 'bg-green-500' : pct > 50 ? 'bg-blue-500' : 'bg-purple-500';
                return (
                  <Tr key={p._id || p.name}>
                    <Td><span className="font-medium text-gray-800 dark:text-gray-200">{p.name}</span></Td>
                    <Td><span className="text-gray-600 dark:text-gray-400 max-w-xs truncate block">{p.description || '-'}</span></Td>
                    <Td>{targetLabel}</Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5" style={{ minWidth: 60 }}>
                          <div className={`${progressColor} h-1.5 rounded-full transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{p.applied_count}/{p.total_target || '?'}</span>
                      </div>
                    </Td>
                    <Td>{formatDate(p.created_at)}</Td>
                    <Td>
                      <Badge variant={p.active ? 'green' : 'gray'}>{p.active ? 'Active' : 'Inactive'}</Badge>
                    </Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setViewingPatch(p)} className="text-purple-600 hover:text-purple-800 dark:text-purple-400 text-xs font-medium flex items-center gap-0.5">
                          <Eye size={12} /> View
                        </button>
                        <button onClick={() => setViewingLogs(p.name)} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 text-xs font-medium flex items-center gap-0.5">
                          <Terminal size={12} /> Logs
                        </button>
                        {p.active && (
                          <button onClick={() => handleDeactivate(p.name)} className="text-red-600 hover:text-red-800 dark:text-red-400 text-xs font-medium">
                            Deactivate
                          </button>
                        )}
                      </div>
                    </Td>
                  </Tr>
                );
              })}
              {patches.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">No scripts deployed yet</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card>
      </div>

      {/* View Script Modal */}
      {viewingPatch && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setViewingPatch(null)}>
          <div className="bg-white dark:bg-[#111] rounded-lg p-5 w-full max-w-2xl mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-800 dark:text-white">{viewingPatch.name}</h2>
              <button onClick={() => setViewingPatch(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X size={18} />
              </button>
            </div>
            <pre className="bg-gray-900 text-green-400 rounded-lg p-4 overflow-x-auto text-xs font-mono max-h-96 overflow-y-auto">
              <code>{viewingPatch.script}</code>
            </pre>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setViewingPatch(null)} className="px-3 py-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-800 text-xs">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Execution Logs Modal */}
      {viewingLogs && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setViewingLogs(null)}>
          <div className="bg-white dark:bg-[#111] rounded-lg p-5 w-full max-w-3xl mx-4 shadow-xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-800 dark:text-white">Execution Logs: {viewingLogs}</h2>
              <button onClick={() => setViewingLogs(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X size={18} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              {!logsData?.logs?.length ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-8">No execution logs yet. Logs appear after devices run the script.</p>
              ) : (
                <div className="space-y-2">
                  {logsData.logs.map((log, i) => (
                    <div key={i} className={`border rounded-lg p-3 ${log.status === 'failed' ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10' : 'border-gray-200 dark:border-gray-700'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${log.status === 'failed' ? 'bg-red-500' : 'bg-green-500'}`} />
                          <span className="text-xs font-medium text-gray-800 dark:text-gray-200">{log.client_name || log.client_id}</span>
                          <span className="text-[10px] text-gray-400 font-mono">{log.client_id}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {log.exit_code != null && (
                            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${log.exit_code === 0 ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'}`}>
                              exit {log.exit_code}
                            </span>
                          )}
                          <span className="text-[10px] text-gray-400">{formatDate(log.applied_at)}</span>
                        </div>
                      </div>
                      {log.output && (
                        <pre className="bg-gray-900 text-gray-300 rounded p-2 text-[10px] font-mono max-h-32 overflow-y-auto mt-1 whitespace-pre-wrap">
                          {log.output}
                        </pre>
                      )}
                      {!log.output && log.exit_code == null && (
                        <p className="text-[10px] text-gray-400 italic mt-1">Acknowledged — output pending or not captured</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className="text-xs text-gray-500">{logsData?.total || 0} total executions</span>
              <button onClick={() => setViewingLogs(null)} className="px-3 py-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-800 text-xs">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
